import {
  useGetDownloadSignedUrlForQuestionGroupsLazyQuery,
  useGetDownloadSignedUrlForQuestionsLazyQuery,
} from '@exam-management/graphql';
import { useCallback } from 'react';

import type {
  QSetQuestion,
  Question,
  QuestionFileName,
  QuestionGroupFileName,
  QuestionWithSnapshot,
  QSetQuestionWithQuestion,
  QSetQuestionWithQuestionSnapshot,
} from '../../types';
import {
  getQSetQuestionsFileNames,
  getQSetQuestionsSnapshotFileNames,
  updateImageTags,
} from '../../utils';

export function useGetQSetQuestionsWithImage() {
  const [getDownloadSignedUrlForQuestions, { loading: isGettingDownloadSignedUrlForQuestions }] =
    useGetDownloadSignedUrlForQuestionsLazyQuery();
  const [
    getDownloadSignedUrlForQuestionGroups,
    { loading: isGettingDownloadSignedUrlForQuestionGroups },
  ] = useGetDownloadSignedUrlForQuestionGroupsLazyQuery();

  const mapQuestion = useCallback(
    (
      question: Question | QuestionWithSnapshot,
      questionAssetMap: Record<string, string>,
      questionGroupAssetMap: Record<string, string>,
    ) => {
      return {
        ...question,
        details: updateImageTags(question.details, questionAssetMap ?? {}),
        solution: {
          ...question.solution,
          value: updateImageTags(question.solution?.value ?? '', questionAssetMap ?? {}),
        },
        questionGroup: question.questionGroup
          ? {
              ...question.questionGroup,
              metadata: {
                ...question.questionGroup?.metadata,
                paragraph: updateImageTags(
                  question.questionGroup?.metadata?.paragraph ?? '',
                  questionGroupAssetMap,
                ),
              },
            }
          : null,
        metadata: {
          ...question.metadata,
          reference: updateImageTags(question.metadata.reference ?? '', questionAssetMap ?? {}),
          choices: question.metadata.choices.map((choice) => ({
            ...choice,
            label: updateImageTags(choice.label, questionAssetMap ?? {}),
          })),
        },
      };
    },
    [],
  );

  const getSignedUrl = useCallback(
    async (questions: QuestionFileName[], questionGroups: QuestionGroupFileName[]) => {
      const questionContentMapResult = await getDownloadSignedUrlForQuestions({
        variables: {
          input: {
            questionFileInputs: questions,
          },
        },
      });

      const questionGroupContentMapResult = await getDownloadSignedUrlForQuestionGroups({
        variables: {
          input: {
            questionGroupFileInputs: questionGroups.map((input) => ({
              questionGroupId: input.id,
              fileNames: input.fileNames,
            })),
          },
        },
      });

      const questionContentMap =
        questionContentMapResult.data?.qctGetDownloadSignedUrlForQuestions?.signedUrls;
      const questionGroupContentMap =
        questionGroupContentMapResult.data?.qctGetDownloadSignedUrlForQuestionGroups?.signedUrls;
      return { questionContentMap, questionGroupContentMap };
    },
    [getDownloadSignedUrlForQuestions, getDownloadSignedUrlForQuestionGroups],
  );

  const getQSetQuestionsWithImage = useCallback(
    async (qSetQuestions: QSetQuestion[]) => {
      const { questions, questionGroups } = getQSetQuestionsFileNames(qSetQuestions);
      const { questionContentMap, questionGroupContentMap } = await getSignedUrl(
        questions,
        questionGroups,
      );

      const qSetQuestionsWithImage = qSetQuestions.map((qSetQuestion) => {
        const { question } = qSetQuestion;

        if (!question) {
          return qSetQuestion;
        }

        const questionAssetMap: Record<string, string> = questionContentMap[question.id];
        const questionGroupAssetMap = (
          question?.questionGroup?.id ? questionGroupContentMap[question.questionGroup.id] : {}
        ) as Record<string, string>;

        return {
          ...qSetQuestion,
          question: mapQuestion(question, questionAssetMap, questionGroupAssetMap),
        };
      });
      return qSetQuestionsWithImage as QSetQuestionWithQuestion[];
    },
    [getSignedUrl, mapQuestion],
  );

  const getQSetQuestionsSnapshotWithImage = useCallback(
    async (qSetQuestions: QSetQuestion[]) => {
      const { questions, questionGroups } = getQSetQuestionsSnapshotFileNames(qSetQuestions);
      const { questionContentMap, questionGroupContentMap } = await getSignedUrl(
        questions,
        questionGroups,
      );

      const qSetQuestionsWithImage = qSetQuestions.map((qSetQuestion) => {
        const { questionSnapshot, questionId, questionGroupId } = qSetQuestion;

        if (!questionSnapshot) {
          return qSetQuestion;
        }

        const questionAssetMap: Record<string, string> = questionContentMap[questionId];
        const questionGroupAssetMap: Record<string, string> =
          questionGroupContentMap[questionGroupId];

        return {
          ...qSetQuestion,
          questionSnapshot: mapQuestion(questionSnapshot, questionAssetMap, questionGroupAssetMap),
        };
      });
      return qSetQuestionsWithImage as QSetQuestionWithQuestionSnapshot[];
    },
    [getSignedUrl, mapQuestion],
  );

  return {
    getQSetQuestionsWithImage,
    getQSetQuestionsSnapshotWithImage,
    loading: isGettingDownloadSignedUrlForQuestions || isGettingDownloadSignedUrlForQuestionGroups,
  };
}
